import { calculateCreatorReceive, getPlatformFeeRate } from "../../services/dropPublishService";

export const MAX_VIEWS = 100;

export function formatMoney(amount, { compact = false } = {}) {
  const n = Number(amount) || 0;
  if (compact && n >= 1000) return `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K`;
  return `$${n.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatMoneyWhole(amount) {
  const n = Number(amount) || 0;
  return `$${Math.round(n).toLocaleString("en-CA")}`;
}

export { formatCount } from "../../utils/formatCount";

export function dropViewCount(drop) {
  return Number(drop.viewCount ?? 0);
}

export function dropMaxViews(drop) {
  return Number(drop.maxViews ?? MAX_VIEWS);
}

export function getDropStatus(drop) {
  if (drop.status === "draft") return "draft";
  const views = dropViewCount(drop);
  const max = dropMaxViews(drop);
  if (drop.status === "closed" || views >= max) return "closed";
  if (drop.status === "open" || !drop.status) return "open";
  return drop.status;
}

export function dropProgress(drop) {
  const views = dropViewCount(drop);
  const max = dropMaxViews(drop);
  return Math.min(100, Math.round((views / max) * 100));
}

export function dropEarned(drop, userProfile) {
  if (drop.isFree || !drop.price) return 0;
  const unlocks = dropViewCount(drop);
  const gross = unlocks * Number(drop.price);
  return calculateCreatorReceive(gross, userProfile).net;
}

export function dropGross(drop) {
  if (drop.isFree || !drop.price) return 0;
  return dropViewCount(drop) * Number(drop.price);
}

export function timeAgo(timestamp) {
  if (!timestamp) return "—";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  return date.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

export function formatDate(timestamp) {
  if (!timestamp) return "—";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

export function contentTypeLabel(type) {
  const map = { video: "Video", photo: "Photo", audio: "Audio", article: "Article" };
  return map[type] || "Drop";
}

export function greetingName(displayName) {
  const first = (displayName || "Creator").split(" ")[0];
  const hour = new Date().getHours();
  const period = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  return { period, name: first };
}

export function nextPayoutDate() {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return next.toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" });
}

export function computeMonthlyEarnings(drops, userProfile, monthOffset = 0) {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - monthOffset, 1);
  const end = new Date(now.getFullYear(), now.getMonth() - monthOffset + 1, 0, 23, 59, 59);

  let gross = 0;
  drops.forEach((drop) => {
    if (drop.isFree) return;
    const created = drop.createdAt?.toDate?.() || null;
    if (!created || created < start || created > end) return;
    gross += dropGross(drop);
  });

  const net = calculateCreatorReceive(gross, userProfile).net;
  const feeRate = getPlatformFeeRate(userProfile);
  const platformFee = gross * feeRate;
  const stripeFee = gross * 0.029 + (gross > 0 ? 0.3 : 0);

  return { gross, net, platformFee, stripeFee, feeRate };
}

export function computeTotalEarnings(drops, userProfile) {
  let gross = 0;
  drops.forEach((drop) => {
    if (!drop.isFree && drop.price) gross += dropGross(drop);
  });
  return { gross, net: calculateCreatorReceive(gross, userProfile).net };
}
